import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation } from '@remix-run/react';

import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { useCommunityDisplayName } from '../../_hooks/useCommunityDisplayName';
import { usePurchasableTiersForIds } from '../../_hooks/useCommunityPurchasableTiers';
import type { UpgradeHandoff } from '../../_hooks/useUpgradeHandoff';
import { BaseUpgradeModal } from '../BaseUpgradeModal';
import { pollAccessRoute } from '../BaseUpgradeModal/useAccessPoll';
import type { ContentUpgradePurchase } from './useContentUpgradeHandoff';

interface ContentUpgradeModalProps {
  purchase: ContentUpgradePurchase;
  tierIds: string[];
  resourceSlug?: string;
  handoff: UpgradeHandoff<ContentUpgradePurchase>;
}

export const ContentUpgradeModal: FC<ContentUpgradeModalProps> = ({
  purchase,
  tierIds,
  resourceSlug,
  handoff: { awaiting, markAwaitingAccess, onAccessGranted },
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const communityName = useCommunityDisplayName();
  const pricing = usePurchasableTiersForIds(tierIds, true);
  const checkAccess = useMemo(() => {
    const params = new URLSearchParams({ page_slug: purchase.pageSlug });
    if (resourceSlug) params.set('resource_slug', resourceSlug);
    return pollAccessRoute(`/content_page_access?${params}`, 'content_accessible');
  }, [purchase.pageSlug, resourceSlug]);

  return (
    <BaseUpgradeModal
      isOpen
      title={t('community.upgrade_modal.content_title', { name: purchase.name })}
      description={t('community.upgrade_modal.content_description', { communityName })}
      impressionPageBlock={NectarPageBlock.CommunityContentUpgradeModal}
      pricing={pricing}
      checkAccess={checkAccess}
      isAwaitingAccess={awaiting?.pageSlug === purchase.pageSlug && awaiting.resourceId === purchase.resourceId}
      onPaymentAccepted={() => markAwaitingAccess(purchase)}
      onAccessGranted={onAccessGranted}
      successRedirectTo={pathname}
    />
  );
};
